import { useState, useEffect } from "react";
import Swal from "sweetalert2";
import {
  LayoutDashboard,
  Pill,
  Tablets,
  Hospital,
  Store,
  Users,
  Truck,
  ClipboardList,
  Archive,
  CircleDollarSign,
  Activity,
  Package,
  Boxes,
  ReceiptText,
  ClipboardCheck,
  Scale,
  ScrollText,
  ChartNoAxesCombined,
  ChartBarStacked,
} from "lucide-react";
import { FaChevronRight } from "react-icons/fa";
import { NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

const Sidebar = ({ isCollapsed, isMobileOpen, onClose }) => {
  const [openMenu, setOpenMenu] = useState(null);
  const [role, setRole] = useState(localStorage.getItem("role"));
  const [ripples, setRipples] = useState({});
  const [subRipples, setSubRipples] = useState({});
  const location = useLocation();

  useEffect(() => {
    const storedRole = localStorage.getItem("role");
    setRole(storedRole);
  }, []);

  const menuItems = [
    {
      to: "/",
      end: true,
      label: "Dashboard",
      icon: <LayoutDashboard className="w-4 h-4" />,
      release: true,
    },
    {
      label: "Enterprise",
      icon: <Hospital className="w-4 h-4" />,
      children: [
        {
          to: "/enterprise/outlet",
          label: "Outlets",
          icon: <Store className="w-4 h-4" />,
          roles: ["owner"],
          release: true,
        },
        {
          to: "/enterprise/shift",
          label: "Shift",
          icon: <ClipboardList className="w-4 h-4" />,
          roles: ["owner"],
          release: true,
        },
        {
          to: "/enterprise/karyawan",
          label: "Karyawan",
          icon: <Users className="w-4 h-4" />,
          roles: ["owner", "manager"],
          release: true,
        },
        {
          to: "/enterprise/distributor",
          label: "Distributor",
          icon: <Truck className="w-4 h-4" />,
          roles: ["owner"],
          release: true,
        },
      ],
    },

    {
      label: "Inventory",
      icon: <Archive className="w-4 h-4" />,
      children: [
        {
          to: "/inventory/stock-opname",
          label: "Stock Opname",
          icon: <Boxes className="w-4 h-4" />,
          roles: ["owner", "manager"],
          release: true,
        },
        {
          to: "/inventory/purchase-order",
          label: "Purchase Order",
          icon: <ClipboardCheck className="w-4 h-4" />,
          roles: ["owner", "manager"],
          release: true,
        },
        {
          to: "/inventory/invoice",
          label: "Invoice",
          icon: <ReceiptText className="w-4 h-4" />,
          roles: ["owner", "manager"],
          release: true,
        },
      ],
    },

    {
      label: "Obat & Paket",
      icon: <Pill className="w-4 h-4" />,
      children: [
        {
          label: "Obat",
          to: "/obat-paket/obat",
          icon: <Tablets className="w-4 h-4" />,
          roles: ["owner", "manager"],
          release: true,
        },
        {
          label: "Paket",
          to: "/obat-paket/paket",
          icon: <Package className="w-4 h-4" />,
          roles: ["owner", "manager"],
          release: true,
        },
      ],
    },

    {
      label: "Transaction",
      to: "/transactions",
      icon: <CircleDollarSign className="w-4 h-4" />,
      end: true,
      release: true,
    },

    {
      label: "Detailed Report",
      icon: <ScrollText className="w-4 h-4" />,
      children: [
        {
          label: "Sales Report",
          icon: <ChartNoAxesCombined className="w-4 h-4" />,
          to: "/report/salesreport",
          roles: ["owner", "manager"],
          release: true,
          end: true,
        },
        {
          label: "Monthly Report",
          icon: <ChartBarStacked className="w-4 h-4" />,
          to: "/report/monthlyreport",
          roles: ["owner", "manager"],
          release: true,
          end: true,
        },
      ],
    },

    {
      label: "Rebalance",
      to: "/rebalance",
      icon: <Scale className="w-4 h-4" />,
      roles: ["owner", "manager"],
      release: true,
    },

    {
      label: "Logs",
      to: "/logs",
      icon: <Activity className="w-4 h-4" />,
      roles: ["owner", "manager"],
      release: true,
    },
  ];

  // Auto-expand menu if sub route matches current location
  useEffect(() => {
    menuItems.forEach((menu) => {
      if (menu.children) {
        const hasActiveChild = menu.children.some((child) =>
          location.pathname.startsWith(child.to)
        );
        if (hasActiveChild) {
          setOpenMenu(menu.label);
        }
      }
    });
  }, [location.pathname]);

  const createRipple = (event, idx, type = "main") => {
    const rect = event.currentTarget.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height) * 2;
    const x = event.clientX - rect.left - size / 2;
    const y = event.clientY - rect.top - size / 2;

    const newRipple = { x, y, size, id: Date.now() };

    if (type === "main") {
      setRipples((prev) => ({
        ...prev,
        [idx]: [...(prev[idx] || []), newRipple],
      }));
      setTimeout(() => {
        setRipples((prev) => ({
          ...prev,
          [idx]: (prev[idx] || []).filter((r) => r.id !== newRipple.id),
        }));
      }, 500);
    } else {
      setSubRipples((prev) => ({
        ...prev,
        [idx]: [...(prev[idx] || []), newRipple],
      }));
      setTimeout(() => {
        setSubRipples((prev) => ({
          ...prev,
          [idx]: (prev[idx] || []).filter((r) => r.id !== newRipple.id),
        }));
      }, 500);
    }
  };

  const toggleMenu = (label) => {
    setOpenMenu(openMenu === label ? null : label);
  };

  const handleClickMenuOrSubMenu = (
    event,
    idx,
    type = "main",
    released = true
  ) => {
    createRipple(event, idx, type);
    if (onClose) onClose();

    if (!released) {
      event.preventDefault();
      Swal.fire({
        title: "Coming Soon",
        text: "Fitur ini akan segera hadir!",
        icon: "info",
        confirmButtonColor: "#0D9488",
      });
    }
  };

  return (
    <>
      {/* Mobile Backdrop Overlay */}
      <AnimatePresence>
        {isMobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm z-40 lg:hidden"
          />
        )}
      </AnimatePresence>

      <aside
        className={`w-64 fixed left-0 top-0 bg-white/95 backdrop-blur-md border-r border-gray-100 shadow-xl shadow-gray-200/40 h-full flex flex-col justify-between z-50 transition-transform duration-300 ${
          isMobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
      <div className="overflow-y-auto flex-1 p-4 space-y-6">
        {/* Brand Header */}
        <div className="flex items-center gap-3 px-2 pt-2">
          <div className="w-10 h-10 rounded-2xl bg-gradient-to-tr from-emerald-600 to-teal-500 text-white font-extrabold flex items-center justify-center text-lg shadow-lg shadow-teal-500/20">
            ➕
          </div>
          <div>
            <div className="flex items-center gap-1">
              <span className="text-xl font-extrabold tracking-tight text-gray-900">
                Djaya
              </span>
              <span className="text-xl font-extrabold tracking-tight text-teal-600">
                Medika
              </span>
            </div>
            <span className="text-[11px] font-semibold text-gray-400 block tracking-wide">
              Pharmacy System
            </span>
          </div>
        </div>

        {/* Navigation Menu */}
        <nav className="flex flex-col gap-1.5">
          {menuItems.map((menu, idx) =>
            menu.children ? (
              <div key={idx} className="space-y-1">
                <div
                  className={`group flex items-center justify-between p-3 rounded-2xl cursor-pointer text-sm font-semibold transition-all duration-200 ${
                    openMenu === menu.label
                      ? "bg-teal-50/70 text-teal-700"
                      : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                  }`}
                  onClick={() => toggleMenu(menu.label)}
                >
                  <div className="flex items-center gap-3">
                    <span
                      className={`p-1.5 rounded-xl transition-colors ${
                        openMenu === menu.label
                          ? "bg-teal-600 text-white"
                          : "text-gray-400 group-hover:text-teal-600"
                      }`}
                    >
                      {menu.icon}
                    </span>
                    <span>{menu.label}</span>
                  </div>
                  <FaChevronRight
                    className={`text-xs transition-transform duration-200 ${
                      openMenu === menu.label
                        ? "rotate-90 text-teal-600"
                        : "text-gray-400"
                    }`}
                  />
                </div>

                <AnimatePresence>
                  {openMenu === menu.label && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25 }}
                      className="overflow-hidden pl-4 pr-1"
                    >
                      <div className="flex flex-col gap-1 py-1 border-l-2 border-teal-100 pl-3">
                        {menu.children
                          .filter(
                            (sub) => !sub.roles || sub.roles.includes(role)
                          )
                          .map((subMenu, subIdx) => (
                            <NavLink
                              key={subIdx}
                              to={subMenu.to}
                              end={subMenu.end}
                              className={({ isActive }) =>
                                `relative px-3 py-2.5 rounded-xl flex items-center gap-2.5 text-xs font-semibold overflow-hidden transition-all duration-200 ${
                                  isActive
                                    ? "bg-teal-600 text-white shadow-md shadow-teal-600/20"
                                    : "text-gray-600 hover:bg-teal-50/80 hover:text-teal-700"
                                }`
                              }
                              onClick={(event) =>
                                handleClickMenuOrSubMenu(
                                  event,
                                  `${idx}-${subIdx}`,
                                  "sub",
                                  subMenu.release
                                )
                              }
                            >
                              {subMenu.icon}
                              <span>{subMenu.label}</span>
                            </NavLink>
                          ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <NavLink
                to={menu.to}
                key={menu.to}
                end={menu.end}
                className={({ isActive }) =>
                  `relative p-3 rounded-2xl flex items-center gap-3 text-sm font-semibold overflow-hidden transition-all duration-200 ${
                    isActive
                      ? "bg-gradient-to-r from-teal-600 to-emerald-600 text-white shadow-lg shadow-teal-600/20"
                      : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                  }`
                }
                onClick={(event) =>
                  handleClickMenuOrSubMenu(event, idx, "main", menu.release)
                }
              >
                {menu.icon}
                <span>{menu.label}</span>
              </NavLink>
            )
          )}
        </nav>
      </div>

      {/* Footer Info / Role Badge */}
      <div className="p-4 border-t border-gray-100 bg-gray-50/50">
        <div className="flex items-center gap-3 p-2.5 rounded-2xl bg-white border border-gray-100 shadow-sm">
          <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
          <div className="flex flex-col">
            <span className="text-xs font-bold text-gray-800 uppercase tracking-wider">
              {role || "User"} Mode
            </span>
            <span className="text-[10px] text-gray-400 font-medium">
              Djaya Medika v1.0
            </span>
          </div>
        </div>
      </div>
    </aside>
    </>
  );
};

export default Sidebar;
